import { useState, useRef, useEffect } from 'react';
import Portal from '../atoms/Portal';
import { ChevronDown, X, Check } from 'lucide-react';

export default function MultiSelect({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'Select items...',
  maxVisibleTags = 1,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 220 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const toggleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (val) => {
    let updated;
    if (value.includes(val)) {
      updated = value.filter((v) => v !== val);
    } else {
      updated = [...value, val];
    }
    if (onChange) {
      onChange(updated);
    }
  };

  const removeTag = (e, val) => {
    e.stopPropagation();
    if (onChange) {
      onChange(value.filter((v) => v !== val));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(e.target);

      if (isOutsideTrigger && isOutsidePopover) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Tags show the option's label, falling back to the raw value for plain-string options.
  const labelOf = (val) => {
    const match = options.find((opt) => typeof opt === 'object' && opt.value === val);
    return match ? match.label : val;
  };

  const renderTagsContent = () => {
    if (value.length === 0) {
      return (
        <span className="custom-dropdown-trigger__label custom-dropdown-trigger__label--placeholder">
          {placeholder}
        </span>
      );
    }

    if (value.length <= maxVisibleTags + 1) {
      return (
        <div className="multi-select-trigger__tags">
          {value.map((val) => (
            <span key={val} className="multi-select-trigger__tag">
              <span>{labelOf(val)}</span>
              <span className="multi-select-trigger__tag-remove" onClick={(e) => removeTag(e, val)}>
                <X size={12} />
              </span>
            </span>
          ))}
        </div>
      );
    }

    const visibleItems = value.slice(value.length - maxVisibleTags);
    const hiddenCount = value.length - maxVisibleTags;

    return (
      <div className="multi-select-trigger__tags">
        <span className="multi-select-trigger__tag-count">+{hiddenCount}</span>
        {visibleItems.map((val) => (
          <span key={val} className="multi-select-trigger__tag">
            <span>{labelOf(val)}</span>
            <span className="multi-select-trigger__tag-remove" onClick={(e) => removeTag(e, val)}>
              <X size={12} />
            </span>
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={`form-group ${className}`} ref={triggerRef}>
      {label && <label>{label}</label>}
      <div
        className={`multi-select-trigger ${isOpen ? 'multi-select-trigger--open' : ''}`}
        onClick={toggleOpen}
        tabIndex={0}
      >
        {renderTagsContent()}
        <ChevronDown size={16} className="custom-dropdown-trigger__icon" />
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={popoverRef}
            className="portal-popover custom-dropdown-menu"
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width,
            }}
          >
            {options.map((opt, idx) => {
              const val = typeof opt === 'object' ? opt.value : opt;
              const lbl = typeof opt === 'object' ? opt.label : opt;
              const isSelected = value.includes(val);
              return (
                <div
                  key={idx}
                  className={`dropdown-item ${isSelected ? 'dropdown-item--selected' : ''}`}
                  onClick={() => handleSelect(val)}
                >
                  <span>{lbl}</span>
                  {isSelected && <Check size={14} />}
                </div>
              );
            })}
          </div>
        </Portal>
      )}
    </div>
  );
}
